import React, { useState, useRef, useCallback } from 'react';
import styles from './Job.module.css';
import LineworxClient from '../lineworxClient';
import DragAndDrop from '../DragNDrop';

const JobImages = ({ images, onImageDeleted, ticketId, poleName, lineName }) => {
    const [selectedImage, setSelectedImage] = useState(null);
    const [deletingImage, setDeletingImage] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [cursorPosition, setCursorPosition] = useState(null);
    const [showAbove, setShowAbove] = useState(false);
    const fileInputRef = useRef(null);
    const gridRef = useRef(null);
    const modalRef = useRef(null);
    const dragCounterRef = useRef(0);

    const handleImageClick = (image) => {
        setSelectedImage(image);
    };

    const handleCloseModal = () => {
        setSelectedImage(null);
    };

    const handleDeleteClick = async (image, e) => {
        e.stopPropagation(); // Prevent opening the modal when clicking delete
        if (window.confirm('Are you sure you want to delete this image?')) {
            setDeletingImage(image.id);
            try {
                const response = await LineworxClient.deleteTicketImage(image.id);
                if (response.ok) {
                    if (onImageDeleted) {
                        const updatedImages = images.filter(img => img.id !== image.id);
                        onImageDeleted(updatedImages);
                    }
                } else {
                    alert('Failed to delete image. Please try again.');
                }
            } catch (error) {
                console.error('Error deleting image:', error);
                alert('Error deleting image. Please try again.');
            } finally {
                setDeletingImage(null);
            }
        }
    };

    const handleImageUpload = async (files) => {
        if (!files || files.length === 0) return;
        
        if (files.length > 10) {
            alert('You can only upload up to 10 images total. Please select fewer files.');
            return;
        }
        
        const totalFiles = images.length + files.length;
        if (totalFiles > 10) {
            alert('You can only upload up to 10 images total. Please select fewer files.');
            return;
        }
        
        setUploadingImage(true);
        try {
            // Process all files in parallel
            const uploadPromises = Array.from(files).map(async (file) => {
                // Get signed URL for S3 upload
                const signedRequestResponse = await LineworxClient.getSignedRequest();
                if (!signedRequestResponse) {
                    throw new Error('Failed to get signed URL for upload');
                }
                
                // Upload to S3
                const uploadResponse = await LineworxClient.uploadFileToS3(signedRequestResponse.signedRequest, file);
                if (!uploadResponse.ok) {
                    throw new Error(`Failed to upload image to S3: ${uploadResponse.statusText}`);
                }
                
                // Notify server about the new image
                const formData = new FormData();
                formData.append('ticketId', ticketId.toString());
                formData.append('url', signedRequestResponse.url);
                formData.append('thumbUrl', signedRequestResponse.url);
                formData.append('isVideo', 'false');
                
                const completeResponse = await LineworxClient.uploadTicketImage(formData);
                if (!completeResponse.ok) {
                    throw new Error('Failed to save image to server');
                }

                // Return the new image object
                return {
                    id: completeResponse.body.id,
                    url: signedRequestResponse.url,
                    thumbnailurl: signedRequestResponse.url,
                    pole_name: poleName || '',
                    line_name: lineName || '',
                    uploaded_at: new Date().toISOString()
                };
            });

            // Wait for all uploads to complete
            const newImages = await Promise.all(uploadPromises);
            
            // Update the parent component with all new images
            if (onImageDeleted) {
                const currentImages = images.filter(img => img && img.id);
                const updatedImages = [...currentImages, ...newImages];
                onImageDeleted(updatedImages);
            }
        } catch (error) {
            console.error('Error uploading images:', error);
            alert(`Error uploading images: ${error.message || 'Please try again.'}`);
        } finally {
            setUploadingImage(false);
        }
    };

    const handleFileSelect = () => {
        fileInputRef.current?.click();
    };

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (isDragging) {
            const container = modalRef.current || gridRef.current;
            if (container) {
                const rect = container.getBoundingClientRect();
                const y = e.clientY - rect.top;
                const containerHeight = rect.height;
                // If within 390px from top, always show below
                if (y < 390) {
                    setShowAbove(false);
                } else {
                    // Show above if within 100px of bottom, below if within 100px of top
                    const isNearBottom = y > (containerHeight - 100);
                    const isNearTop = y < 100;
                    setShowAbove(isNearBottom || !isNearTop);
                }
                setCursorPosition(y);
            }
        }
    }, [isDragging]);

    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current += 1;
        if (dragCounterRef.current === 1) {
            setIsDragging(true);
            const container = modalRef.current || gridRef.current;
            if (container) {
                const rect = container.getBoundingClientRect();
                const y = e.clientY - rect.top;
                const containerHeight = rect.height;
                if (y < 390) {
                    setShowAbove(false);
                } else {
                    const isNearBottom = y > (containerHeight - 100);
                    const isNearTop = y < 100;
                    setShowAbove(isNearBottom || !isNearTop);
                }
                setCursorPosition(y);
            }
        }
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current -= 1;
        if (dragCounterRef.current === 0) {
            setIsDragging(false);
            setCursorPosition(null);
            setShowAbove(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current = 0;
        setIsDragging(false);
        setCursorPosition(null);
        setShowAbove(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {         
            if (selectedImage) {
                setSelectedImage(null);
            }
            handleImageUpload(e.dataTransfer.files);
        }
    }, [selectedImage, handleImageUpload, images]); 

    
    const memoizedHandleImageUpload = useCallback(handleImageUpload, [images, ticketId, poleName, lineName, onImageDeleted]);

    return (
        <div className={styles.imagesContainer}>
            {/* Upload Section */}
            <div className={styles.uploadSection}>
                <DragAndDrop
                    handleDrop={memoizedHandleImageUpload}
                    handleFileSelect={handleFileSelect}
                    file={{}}
                    copy="Drag & Drop Images (limit 10 files)"
                    multiple={true}
                />
                <div style={{display: 'none'}}>
                    <input 
                        type="file" 
                        ref={fileInputRef}
                        accept="image/*"
                        multiple
                        onChange={(e) => memoizedHandleImageUpload(e.target.files)}
                    />
                </div>
            </div>

            {/* Image Grid with Drag and Drop */}
            <div 
                ref={gridRef}
                className={`${styles.imageGrid} ${isDragging ? styles.dragging : ''}`}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {images.map((image, index) => (
                    <div 
                        key={image.id} 
                        className={styles.imageItem}
                        onClick={() => handleImageClick(image)}
                    >
                        <img 
                            src={image.thumbnailurl || image.url} 
                            alt={`${image.pole_name} - ${image.line_name}`}
                            className={styles.thumbnail}
                        />
                        <div className={styles.imageInfo}>
                            <div>{image.pole_name}</div>
                            <div>{image.line_name}</div>
                        </div>
                        <button 
                            className={styles.deleteButton}
                            onClick={(e) => handleDeleteClick(image, e)}
                            disabled={deletingImage === image.id}
                        >
                            {deletingImage === image.id ? '...' : '×'}
                        </button>
                    </div>
                ))}
                {isDragging && cursorPosition !== null && (
                    <div 
                        className={`${styles.dragOverlay} ${showAbove ? styles.above : styles.below}`}
                        style={{
                            top: `${cursorPosition}px`
                        }}
                    >
                        <div className={styles.dragMessage}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginBottom: '1rem'}}>
                                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="#24ABFF"/>
                            </svg>
                            <div>Drop images here</div>
                            <div style={{fontSize: '0.9rem', color: '#666', marginTop: '0.5rem'}}>Up to 10 files</div>
                        </div>
                    </div>
                )}
            </div>

            {/* Image Modal with Drag and Drop */}
            {selectedImage && (
                <div 
                    ref={modalRef}
                    className={styles.modal} 
                    onClick={handleCloseModal}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <button className={styles.closeButton} onClick={handleCloseModal}>×</button>
                        <img 
                            src={selectedImage.url} 
                            alt={`${selectedImage.pole_name || poleName || ''} - ${selectedImage.line_name || lineName || ''}`}
                            className={styles.fullImage}
                        />
                        <div className={styles.imageDetails}>
                            <div>Pole: {selectedImage.pole_name || poleName || ''}</div>
                            <div>Line: {selectedImage.line_name || lineName || ''}</div>
                        </div>
                    </div>
                    {isDragging && cursorPosition !== null && (
                        <div 
                            className={`${styles.dragOverlay} ${showAbove ? styles.above : styles.below}`}
                            style={{
                                top: `${cursorPosition}px`
                            }}
                        >
                            <div className={styles.dragMessage}>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginBottom: '1rem'}}>
                                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="#24ABFF"/>
                                </svg>
                                <div>Drop images here</div>
                                <div style={{fontSize: '0.9rem', color: '#666', marginTop: '0.5rem'}}>Up to 10 files</div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Upload Loading Overlay */}
            {uploadingImage && (
                <div className={styles.uploadOverlay}>
                    <div className={styles.uploadSpinner}>
                        <svg style={{animationName: 'spin', animationDuration: '1000ms', animationIterationCount: 'infinite', animationTimingFunction: 'linear'}} width="50" height="50" viewBox="0 0 300 300" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M117.236 117.564L135.488 51.7447C92.1144 58.0338 57.8376 92.408 51.6958 135.829L27.8827 142.432C31.5571 80.9652 80.6763 31.7417 142.099 27.9031L149.836 0L149.836 27.6621V50.7138L149.836 149.836L150 150L182.436 117.564L247.963 135.734C241.785 92.3581 207.526 58.0292 164.185 51.7447L157.574 27.9031C218.965 31.7398 268.066 80.9159 271.784 142.34L300 150.164L150.164 150.164L182.436 182.436L164.279 247.914C207.349 241.626 241.429 207.643 247.863 164.621L271.742 158C267.712 219.081 218.792 267.897 157.666 271.763L149.836 300L149.836 272.01V248.959L149.836 150.491L117.564 182.764L51.7948 164.526C58.1928 207.593 92.292 241.622 135.393 247.914L142.006 271.763C80.8492 267.895 31.9107 219.032 27.9244 157.907L0 150.164L27.6625 150.164H50.7143L149.836 150.164L117.236 117.564Z" fill="#FFCC18"/>
                        </svg>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobImages;